import React, { FC, useContext, useEffect } from 'react';
import { InvoiceTemplate } from '@typings/api-models';
import Spacer from '@components/Spacer';

import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Header from '@components/DesignLibrary/Header';
import LineItemEditor from '@components/LineItemEditor';
import { DEFAULT_CURRENCY } from '@typings/common';
import dashboardContext from '@layouts/DashboardLayout/DashboardContext';
import InvoiceSettings from '@components/InvoiceSettings';
import { ListAndDetailsSubLayoutContext } from '@layouts/ListAndDetailsSubLayout';
import { useRouter } from 'next/router';
import PageContext from '@components/DesignLibrary/PageContext';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import TrashSimple from '@components/Icons/TrashSimple';
import PencilSimple from '@components/Icons/PencilSimple';
import ProgressIndicator from '@components/ProgressIndicator';
import { useToast } from '@/hooks';
import { useOpenWorkflowPane } from '@/panes/workflowHooks';
import { isNoodler } from '@/providers/FeatureFlags/checkFeatureFlag';
import InputField from '@/components/FormFields/InputField';
import Button from '@/components/DesignLibrary/Button';
import Modal from '@/components/Modal';
import { useUserProfile } from '@/providers/Auth';

import s from './CreateEditInvoiceRequestForm.module.scss';

type Props = {
  invoiceTemplate: (Pick<
    InvoiceTemplate,
    'id' | 'title' | 'priceId' | 'isFollowupEnabled' | 'passProcessingFeesToCustomer' | 'isAchPaymentEnabled' | 'isCreditCardPaymentEnabled'
    | 'isDebitCardPaymentEnabled' | 'secondaryStripeAccountId' | 'isPartialPaymentEnabled' | 'confidoBankAccountId' | 'isExemptFromApplicationFee' | 'isDebitCardPaymentRequiredForCustomer'
  > & {
    slug?: string;
    lineItems: Parameters<typeof LineItemEditor>[0]['initialValue'];
  }) | null;
  onClose: () => void;
  beforeClose?: () => Promise<void>;
};

const CreateEditInvoiceRequestForm: FC<Props> = ({ beforeClose, invoiceTemplate, onClose }) => {
  const router = useRouter();
  const { creator } = useContext(dashboardContext);
  const { reloadListView } = useContext(ListAndDetailsSubLayoutContext);
  const [isArchiving, setIsArchiving] = React.useState(false);
  const [invoiceTitle, setInvoiceTitle] = React.useState<string | undefined>(invoiceTemplate?.title);
  const [titleModalVisible, setTitleModalVisible] = React.useState(false);
  const profile = useUserProfile();

  const { getData: createInvoiceTemplate } = useNoodleApi(tsClient.invoices.createInvoiceTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Invoice created successfully!'],
  });
  const { getData: updateTemplate } = useNoodleApi(tsClient.invoices.updateInvoiceTemplate, {
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Invoice updated successfully!'],
  });
  const { getData: updateLineItems } = useNoodleApi(tsClient.invoices.updateTemplateLineItems, {
    toastOnError: true,
  });
  const {
    data: workflows,
    getData: getWorkflowsFn,
    fetchingState: { isFetching: isFetchingWorkflows },
  } = useNoodleApi(tsClient.workflows.getWorkflowsFromBuildingBlock);
  const { getData: archiveInvoiceTemplateFn } = useNoodleApi(tsClient.invoices.archiveInvoiceTemplate);
  const openWorkflowPane = useOpenWorkflowPane();

  const handleSave = async ({ invoiceData, lineItems }: { invoiceData: Omit<Parameters<typeof tsClient.invoices.createInvoiceTemplate>[0], 'creatorId'>; lineItems: Parameters<typeof tsClient.invoices.updateTemplateLineItems>[0]['lineItems'] }): Promise<void> => {
    let templateId;
    if (!creator) {
      return;
    }

    if (invoiceTemplate) {
      await updateTemplate({
        ...invoiceData,
        id: invoiceTemplate.id,
      });
      reloadListView();
      setInvoiceTitle(invoiceData.title);
      templateId = invoiceTemplate.id;
    } else {
      const { data } = await createInvoiceTemplate({
        creatorId: creator.id,
        ...invoiceData,
      });
      if (data) {
        templateId = data.id;
      }
    }
    if (templateId) {
      await updateLineItems({
        id: templateId,
        lineItems,
      });
    }
    await beforeClose?.();
    onClose();
  };

  useEffect(() => {
    if (invoiceTemplate) {
      getWorkflowsFn({ page: 1, perPage: 1000, referenceId: invoiceTemplate.id });
    }
  }, [getWorkflowsFn, invoiceTemplate]);

  const archiveInvoiceTemplate = async (): Promise<void> => {
    if (invoiceTemplate) {
      setIsArchiving(true);
      await archiveInvoiceTemplateFn({ id: invoiceTemplate.id });
      await reloadListView();
      await router.push(getUrl(IDENTIFIERS.DASHBOARD_INVOICE_REQUESTS));
      setIsArchiving(false);
    }
  };

  const uniqueWorkflows = workflows?.items
    ? Array.from(new Map(workflows.items.map(workflow => [workflow.slug, workflow])).values())
    : [];

  return (
    <>
      {invoiceTemplate
        ? (
          <>
            {titleModalVisible && <Modal title="Edit Title" onClose={() => setTitleModalVisible(false)}>
              <div className={s.padding}>
                <InputField id="title" name="title" values={{ title: invoiceTitle }} onChange={setInvoiceTitle} label="Title of invoice" hasFixedHeight />
                <Spacer size={4} />
                {invoiceTemplate.slug && isNoodler(profile) && <InputField id="slug" name="slug" values={{ slug: invoiceTemplate.slug }} onChange={setInvoiceTitle} label="Slug" disabled hasFixedHeight />}
                <Spacer size={8} />
                <Button fullWidth variant='primary' size="md" onClick={() => setTitleModalVisible(false)}>Done</Button>
              </div>
            </Modal>}
            <Header
              secondaryActions={[
                {
                  icon: PencilSimple,
                  label: 'Edit Title',
                  onClick: () => setTitleModalVisible(true),
                },
                {
                  icon: TrashSimple,
                  isDestructive: true,
                  isFetching: isArchiving,
                  label: 'Archive',
                  onClick: archiveInvoiceTemplate,
                },
              ]}
              title={invoiceTitle || 'Invoice'}
              description={invoiceTemplate.slug}
              noDivider
            />
            <div className={s.context}>
              <PageContext
                collapsible
                label={isFetchingWorkflows ? 'workflows' : `Used in ${uniqueWorkflows.length} workflow template${uniqueWorkflows.length === 1 ? '' : 's'}`}
                items={uniqueWorkflows.map?.((workflow) => ({
                  id: workflow.id,
                  onClick: () => openWorkflowPane({ workflowSlug: workflow.slug }),
                  value: workflow.name,
                })) || []}
                isFetching={isFetchingWorkflows}
              />
            </div>
          </>
        )
        : <Spacer size={8} />}
      {isArchiving && (
        <>
          <Spacer size={16} />
          <ProgressIndicator isCentered />
        </>
      )}
      <InvoiceSettings
        currency={creator?.defaultCurrency || DEFAULT_CURRENCY}
        initialValue={invoiceTemplate ? { ...invoiceTemplate, title: invoiceTitle ?? null } : undefined}
        onSave={handleSave}
        requireLineItems={false}
        hideTitle={Boolean(invoiceTemplate)}
      />
    </>
  );
};

export default CreateEditInvoiceRequestForm;
